import { Injectable } from '@angular/core';

import { DeviceCensusModel, HeapSampleModel, NativeDeviceInformationModel } from 'app/shared/models';
import { DevicePlatform } from '@cubigo/digital-signage';

const BYTES_PER_MB = 1024 ** 2;
const CHROMIUM_VERSION = /(?:Chrome|Chromium)\/([0-9]+(?:\.[0-9]+){0,3})/;
const VERSION_ONLY = /^\d{1,4}(?:\.\d{1,4}){0,3}$/;

@Injectable({ providedIn: 'root' })
export class DeviceCensusService {
  public census(
    userAgent: string, width: number, height: number, pixelRatio: number,
    native: NativeDeviceInformationModel | undefined, deviceMemory: unknown
  ): DeviceCensusModel {
    const nativeShell = native?.appVersionName;

    return {
      platform: this.platformFor(userAgent, nativeShell),
      chromiumVersion: this.chromiumVersion(userAgent, native?.chromiumVersion),
      nativeShell: nativeShell && VERSION_ONLY.test(nativeShell) ? nativeShell : undefined,
      screenWidth: width,
      screenHeight: height,
      pixelRatio,
      deviceMemoryGb: typeof deviceMemory === 'number' && Number.isFinite(deviceMemory) ? deviceMemory : undefined
    };
  }

  public heapSample(memory: unknown): HeapSampleModel | undefined {
    if (typeof memory !== 'object' || memory === null || !('usedJSHeapSize' in memory) || !('jsHeapSizeLimit' in memory)) {
      return undefined;
    }

    const used = memory.usedJSHeapSize;
    const limit = memory.jsHeapSizeLimit;

    if (typeof used !== 'number' || typeof limit !== 'number' || !Number.isFinite(used) || !Number.isFinite(limit)
      || used < 0 || limit <= 0) {
      return undefined;
    }

    return { usedJsHeapMb: used / BYTES_PER_MB, heapLimitMb: limit / BYTES_PER_MB };
  }

  private platformFor(userAgent: string, nativeShell?: string): DeviceCensusModel['platform'] {
    if (/CrKey|Chromecast/i.test(userAgent)) {
      return DevicePlatform.Chromecast;
    }

    if (nativeShell || /Android.*(?:TV|AFT|BRAVIA|SHIELD)/i.test(userAgent)) {
      return DevicePlatform.AndroidTv;
    }

    if (/CrOS/i.test(userAgent)) {
      return DevicePlatform.Chromebox;
    }

    return /Chrome|Chromium|Firefox|Safari|Edg\//i.test(userAgent) ? DevicePlatform.Browser : DevicePlatform.Unknown;
  }

  private chromiumVersion(userAgent: string, reported?: string): string | undefined {
    const detected = CHROMIUM_VERSION.exec(userAgent)?.[1];

    if (detected) {
      return detected;
    }

    return reported && VERSION_ONLY.test(reported) ? reported : undefined;
  }
}
